import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import Skeleton from "@mui/material/Skeleton";
import Box from "@mui/material/Box";
import { cardIcon } from "../../../../components/Settings/Billing/helper";
import PaymentMethods from "../../../../components/PaymentMethods";
import { useGetPaymentCards } from "../../../../services/queries";
import {
  Container,
  PaymentInfoContainer,
  MastercardLogo,
  CardDetailsContainer,
  CardNumber,
  ExpiryDate,
  AddPaymentContainer,
  AddPaymentHeader,
  PaymentTitle,
  PaymentDescription,
} from "./PaymentCardView.styled";

/**
 * Skeleton loader for the payment card view
 */
const PaymentCardSkeleton = () => (
  <Container>
    <PaymentInfoContainer>
      <Box width={40} height={40} display="flex" alignItems="center" justifyContent="center">
        <Skeleton 
          variant="circular" 
          width={32} 
          height={32} 
          animation="wave" 
          sx={{ bgcolor: 'rgba(18, 48, 81, 0.1)' }} 
        />
      </Box>
      <CardDetailsContainer>
        <Box width="100%">
          <Skeleton 
            variant="text" 
            width={'80%'} 
            height={20} 
            animation="wave" 
            sx={{ 
              bgcolor: 'rgba(18, 48, 81, 0.1)',
              borderRadius: '4px',
              marginBottom: '4px'
            }} 
          />
        </Box>
        <Box width="100%">
          <Skeleton 
            variant="text" 
            width={'40%'} 
            height={16} 
            animation="wave" 
            sx={{ 
              bgcolor: 'rgba(18, 48, 81, 0.05)',
              borderRadius: '4px'
            }} 
          />
        </Box>
      </CardDetailsContainer>
    </PaymentInfoContainer>
  </Container>
);

/**
 * Self-contained component to display payment method information and handle payment operations
 */
const PaymentCardView = ({
  isPending = false,
  onPaymentMethodChanged = null,
  enabled = true,
}) => {
  const [paymentPopup, setPaymentPopup] = useState(false);

  // Use the custom hook to fetch payment cards
  const { defaultCard, formattedDefaultCard, isLoading, refetch: refetchCards } = useGetPaymentCards({
    enabled: enabled, // Only fetch when component is enabled
  });

  // Pass the default card ID to the parent component when it changes
  useEffect(() => {
    if (onPaymentMethodChanged) {
      // Pass the defaultCard.id or null if no card exists
      onPaymentMethodChanged(defaultCard?.id || null);
    }
  }, [defaultCard, onPaymentMethodChanged]);

  // Display fallback payment info if no cards are found
  const displayPaymentInfo = formattedDefaultCard;

  // Open payment methods popup
  const handleOpenPaymentPopup = () => {
    setPaymentPopup(true);
  };

  // Close payment methods popup
  const handleClosePaymentPopup = () => {
    refetchCards();
    setPaymentPopup(false);
  };

  // Handle when a card is changed, added or removed
  const handleCardChanged = () => {
    refetchCards();
  };

  // Show loading skeleton when data is being fetched
  if (isLoading) {
    return <PaymentCardSkeleton />;
  }

  if (displayPaymentInfo) {
    return (
      <Container>
        <PaymentInfoContainer onClick={handleOpenPaymentPopup}>
          <MastercardLogo>
            <img src={cardIcon[displayPaymentInfo.brand] || cardIcon.mastercard} alt={`${displayPaymentInfo.cardName} Logo`} />
          </MastercardLogo>
          <CardDetailsContainer>
            <CardNumber>
              {displayPaymentInfo.cardName} ending with{" "}
              {displayPaymentInfo.last4}
            </CardNumber>
            <ExpiryDate>Exp. date {displayPaymentInfo.expDate}</ExpiryDate>
          </CardDetailsContainer>
        </PaymentInfoContainer>

        {paymentPopup && (
          <PaymentMethods
            open={paymentPopup}
            handleClose={handleClosePaymentPopup}
            forcePopupClose={false}
            onCardChanged={handleCardChanged}
          />
        )}
      </Container>
    );
  }

  return (
    <Container>
      <AddPaymentContainer onClick={handleOpenPaymentPopup}>
        <AddPaymentHeader>
          <CreditCardIcon style={{ color: "#123051", fontSize: 24 }} />
          <PaymentTitle>Add Payment Method</PaymentTitle>
        </AddPaymentHeader>
        {isPending && (
          <PaymentDescription>
            Payment Information Pending – To proceed with the upgrade, you will
            be prompted to enter your payment details.
          </PaymentDescription>
        )}
      </AddPaymentContainer>

      {paymentPopup && (
        <PaymentMethods
          open={paymentPopup}
          handleClose={handleClosePaymentPopup}
          forcePopupClose={false}
          onCardChanged={handleCardChanged}
        />
      )}
    </Container>
  );
};

PaymentCardView.propTypes = {
  paymentInfo: PropTypes.shape({
    cardName: PropTypes.string.isRequired,
    last4: PropTypes.string.isRequired,
    expDate: PropTypes.string.isRequired,
  }),
  isPending: PropTypes.bool,
  onPaymentMethodChanged: PropTypes.func,
  enabled: PropTypes.bool,
};

export default PaymentCardView;
